import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "./store";
import { OrganizarionService } from "../services/OrganizationService";
import { LinkGenerator, OrganizationStart, OrgUsers, Profit } from "../models/Organization";
import { setListDeposits } from "./DepositsSlice";
import { setOrganizationsWithdrawal } from "./InvoicesSlice";
import CryptoJS from "crypto-js";
import { supplierService } from "../services/SupplierService";
import { setSuppliersOrganization } from "./SuppliersSlice";
import { BankService } from "../services/Bank.Service";
import { BankAcocuntType } from "../models/Banks";
import { UserService } from "../services/UserService";
import { Roles } from "../utils/lists";
import { getOrgId } from "./orgHelpers";

// O enum Roles do front esta alinhado ao AccesRole do backend
// (Roles.owner === 'Owner'), valor aceito pelo @IsEnum(AccesRole) do CreateUserDto.
const OWNER_ROLE = Roles.owner;

export interface NewOwnerInput {
    name: string;
    email: string;
    login: string;
    password?: string;
}

export interface OrganizationType {
    id: number;
    nome: String;
    ativa: String;
    created_at: Date;
}

interface OrganizationState {
    organizations: Array<any>,
    modal: boolean,
    accounts: Array<any> | null,
    listProfit: Profit[] | null,
    clients: OrgUsers[] | null,
    suppliers: Array<any> | null
    link: string | null,
    modalAddClientStatus: boolean,
    profitMonthly: number | null,
    companiesOverview: Array<any> | null
}

const initialState: OrganizationState = {
    organizations : [],
    modal: false,
    accounts: null,
    listProfit: null,
    clients: null,
    suppliers: null,
    link: null,
    modalAddClientStatus: false,
    profitMonthly: null,
    companiesOverview: null
};

const organizationSlice = createSlice({
    name: 'organization',
    initialState,
    reducers: {
        setOrganizations : (state, action: PayloadAction<Array<any>>) => {
            state.organizations = action.payload;
        },
        setModalOrganization: (state, action: PayloadAction<boolean>) => {
            state.modal = action.payload;
        },
        setAccountsList: (state, action: PayloadAction<any[]>) => {
            state.accounts = action.payload;
        },
        setProfitList: (state, action: PayloadAction<Profit[]>) => {
            state.listProfit = action.payload;
        },
        setOrganizationClients : (state, action: PayloadAction<OrgUsers[]>) => {
            state.clients = action.payload;
        },
        setGenerateLink: (state, action: PayloadAction<string>) => {
            state.link = action.payload;
        },
        setModalAddClientStatus: (state, action: PayloadAction<boolean>) => {
            state.modalAddClientStatus = action.payload;
        },
        setSuppliers: (state, action: PayloadAction<Array<any>>) => {
            state.suppliers = action.payload;
        },
        setCompaniesOverview: (state, action: PayloadAction<Array<any>>) => {
            state.companiesOverview = action.payload;
        },
    }
  });

export const {
    setOrganizations,
    setModalOrganization,
    setAccountsList,
    setProfitList,
    setOrganizationClients,
    setGenerateLink,
    setModalAddClientStatus,
    setSuppliers,
    setCompaniesOverview,
} = organizationSlice.actions;

// customs func
export const getOrganizations = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const organizationResponse : any = await OrganizarionService.getAllOrganizations();
    
    if (!organizationResponse) throw new Error('Erro ao tentar pegar organizacoes')

    dispatch(setOrganizations(organizationResponse));
    dispatch(setModalOrganization(false))
}

// Visao geral por empresa (admin): depositos, compras e USDT transacionado.
export const fetchCompaniesOverview = () : AppThunk<Promise<void>> => async (dispatch) => {
    const overview = await OrganizarionService.getCompaniesOverview();
    if (!overview) throw new Error('Erro ao buscar a visao geral das empresas');
    dispatch(setCompaniesOverview(overview));
}

// Cria a empresa e, no mesmo fluxo, o usuario owner dela.
// Passos: 1) cria a organizacao e captura o id gerado; 2) cria o usuario
// com role 'Owner'; 3) vincula o owner a organizacao (o endpoint de create
// nao cria o OrganizationUser). Retorna true em caso de sucesso.
export const addOrganizationWithOwner =
    (name: string, owner: NewOwnerInput): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        // 1. Cria a empresa.
        const orgResponse: any = await OrganizarionService.createOrganization(name);
        const newOrg = orgResponse?.savedOrganization;
        if (!newOrg?.id) throw new Error("Erro ao criar a empresa");

        // 2. Cria o owner e ja vincula a empresa. So enviamos campos aceitos
        //    pelo CreateUserDto (ValidationPipe com forbidNonWhitelisted).
        //    Senha vazia e omitida para o backend gerar uma temporaria.
        //    O endpoint de create faz o link org<->user (isFromRegister=false),
        //    entao nao e preciso chamar /organization/link separado.
        const createdUser: any = await UserService.addUser({
            name: owner.name,
            email: owner.email,
            login: owner.login,
            role: OWNER_ROLE,
            status: "active",
            organizationId: newOrg.id,
            fee: "0",
            password: owner.password ? owner.password : undefined,
        } as any);
        if (!createdUser?.id) throw new Error("Erro ao criar o owner da empresa");

        // 3. Atualiza a lista (tambem fecha o modal via setModalOrganization).
        await dispatch(getOrganizations());
        return true;
    };

export const changeOrganization = (status:boolean, id:number) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    if (!status) throw new Error('Erro ao editar organizacoe - status');

    const organizationResponse = await OrganizarionService.editOrganization({status,id})
    if (!organizationResponse) throw new Error('Erro ao editar organizacoe');

    dispatch(getOrganizations());
}

export const fetchAccounts = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    // Usuario sem organizacao (ex.: cliente recem-registrado) nao tem contas de
    // org — evita uma request malformada para /organizations/accounts/.
    const orgUsers = state.user.userInfo?.organizationUsers ?? [];
    if (orgUsers.length === 0) return;
    const organizationAccounts : any = await OrganizarionService.getOrganizationAccounts(orgUsers);
    dispatch(setAccountsList(organizationAccounts));
}

export const getOrganizationsList = (state: RootState): Array<OrganizationType> | null => state.organization.organizations ?? [];
export const getOrganizationModalStatus = (state: RootState): boolean => state.organization.modal;
export const getCompaniesOverviewList = (state: RootState): Array<any> | null => state.organization.companiesOverview;

export default organizationSlice.reducer;

export const getOrganizationStartInfo = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    dispatch(fetchOrganizationProfit(true));
    dispatch(getOrganizationsUsers());
    const state = getState();
    try {
        const orgStartInfo : OrganizationStart = await OrganizarionService.getAll(getOrgId(state));
        if (!orgStartInfo) throw new Error('Erro ao pegar as infos de start da organizacao');
        dispatch(setListDeposits(orgStartInfo.deposits))
        dispatch(setOrganizationsWithdrawal(orgStartInfo.withdrawals))
    } catch (e) {
        console.error(e);
    }
}


// PROFIT
export const fetchOrganizationProfit = (month:boolean): AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    try {
        if (!state.user.userInfo.organizationUsers) throw new Error('no organization')
        const profit : Profit[] = await OrganizarionService.getOrganizationProfit(getOrgId(state), month);
        if (!profit) throw new Error('Erro ao pegar os lucros da empresa');
        dispatch(setProfitList(profit))
    } catch (e) {
        console.error(e);
    }
}

// USERS
export const getOrganizationsUsers = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    try {
        const orgUser : OrgUsers[] = await OrganizarionService.getUsersFromOrganization(getOrgId(state));
        if (!orgUser) throw new Error('Erro ao pegar os clientes da empresa');
        dispatch(setOrganizationClients(orgUser))
    } catch (e) {
        console.error(e);
    }
}

// Generate Link for Users
export const generateLinkRoutine = (fee:number) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const payload : LinkGenerator = {
        fee,
        orgId: getOrgId(state)
    }

    const encrypt = await encryptJSON(payload, state.auth.secretKey);
    dispatch(setGenerateLink(encrypt))

}

const encryptJSON = (payload : LinkGenerator, secretKey:string) => {
  const jsonString = JSON.stringify(payload);
  const encrypted = CryptoJS.AES.encrypt(jsonString, secretKey).toString();

  const { hostname, origin } = window.location;

  let url = "";

  if (hostname === "localhost") {
    url = `http://localhost:3000/join/key=${encodeURIComponent(encrypted)}`;
  } else {
    url = `${origin}/join/key=${encodeURIComponent(encrypted)}`;
  }

  return url;

};

export const fetchSuppliers = (): AppThunk<Promise<void>> => 
    async (dispatch, getState) => {
        const state = getState();
        const id = getOrgId(state);
        const suppliers = await supplierService.getAllSuppliers(id)
        if (!suppliers) throw new Error('Erro ao tentar pegar os fornecedores')
        dispatch( setSuppliersOrganization(suppliers));
    }

export const fetchAccountByBankAndOrganization =
  (bankId: number): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    
    try {
        const state = getState();

        const organizationId  = state.user.userInfo?.organizationUsers?.[0]?.organization?.id;
        if (!organizationId) throw new Error("Organização não encontrada no estado");

        const response : any = await BankService.getAccountsByIdAndOrganization(bankId,organizationId);
        if (!response) throw new Error( "Erro ao tentar pegar as contas daquela organização");
        
        dispatch(setAccountsList(response[0].accounts));
    } catch (error) {
        console.error("Erro ao buscar contas:", error);
    }
  };